import { connect } from "react-redux";
import { Form } from "formik";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Button, ButtonTypes, Text } from "@/component-library";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import { Checkbox } from "@/component-library/Checkbox/Checkbox";
import { CustomForm } from "@/component-library/CustomForm";
import { FormDateField } from "@/component-library/FormFields/FormDateField/FormDateField";
import { FormPhoneNumberField } from "@/component-library/FormFields/FormPhoneNumberField/FormPhoneNumberField";
import { FormSelect } from "@/component-library/FormFields/FormSelect/FormSelect";
import { FormTextInput } from "@/component-library/FormFields/FormTextInput/FormTextInput";
import { patientRequests } from "@/api/(requests)/patient";
import { showAlertWithAction } from "@/redux/actions/alerts";

import { buildDefaultHCPPatientFormValues } from "../helpers/default-form-values";
import { HCP_VALIDATION_SCHEMA } from "../HCPWorkflow.schema";

const HCPPatientInfo = ({ currentStepName, update_state_and_get_next_node }) => {
  const location = useLocation();
  const has_referred_already = new URLSearchParams(location.search).get(
    "referring_another_patient",
  );

  const [carrierOptions, setCarrierOptions] = useState([]);

  useEffect(() => {
    const getInsuranceList = async () => {
      const result = await patientRequests.fetch_insurance_list({ context: { showLoader: false } });
      const carriersOptionResults = result.carriers.map((carriers) => {
        return { label: carriers, value: carriers };
      });

      setCarrierOptions(carriersOptionResults);
    };

    getInsuranceList();
  }, []);

  const renderForm = (formikProps) => {
    return (
      <Form className="HCPPatientInfo-form" enableReinitialize>
        <div className="HCPWorkflowShared-form_fields">
          <FormTextInput
            className="HCPWorkflowShared-form_field"
            data-private
            formikProps={formikProps}
            name="first_name"
            title="Patient’s Legal First Name"
            triggerErrorOnChange
          />
          <FormTextInput
            className="HCPWorkflowShared-form_field"
            data-private
            formikProps={formikProps}
            name="last_name"
            title="Patient’s Legal Last Name"
            triggerErrorOnChange
          />
          <FormDateField
            className="HCPWorkflowShared-form_field"
            data-private
            formikProps={formikProps}
            name="dob"
            placeholder="MM/DD/YYYY"
            title="Date of Birth"
            triggerErrorOnChange
          />
          <FormPhoneNumberField
            className="HCPWorkflowShared-form_field"
            data-private
            formikProps={formikProps}
            name="mobile"
            placeholder="(555)-555-5555"
            title={
              <>
                <Text className="font-weight-bold">Mobile Phone</Text>{" "}
                <Text className="font-weight-medium">(Optional)</Text>
              </>
            }
            triggerErrorOnChange
          />
          <FormTextInput
            className="HCPWorkflowShared-form_field"
            data-private
            formikProps={formikProps}
            name="email"
            title="Patient’s Email "
            triggerErrorOnChange
          />
          <FormTextInput
            className="HCPWorkflowShared-form_field"
            data-private
            formikProps={formikProps}
            name="email_validation"
            title="Confirm Patient’s Email"
            triggerErrorOnChange
          />
          <div className="HCPPatientInfo-has_insurance_info-container HCPWorkflowShared-form_field">
            <FormSelect
              className="HCPWorkflowShared-form_field"
              dataPrivate={true}
              disabled={formikProps.values.does_not_have_insurance_information}
              formikProps={formikProps}
              name="insurance_company"
              options={carrierOptions}
              placeholder="Search Insurance Company"
              testId="HCPPatientInsuranceDropdown"
              title="Insurance Company"
              triggerErrorOnChange
            />

            <Checkbox
              checkboxContainerClassName="HCPPatientInfo-has_insurance_info-checkbox_container"
              checked={formikProps.values.does_not_have_insurance_information}
              data-private
              label="I don’t have the patient’s insurance information"
              labelClassName="HCPPatientInfo-has_insurance_info-label"
              onChange={() => {}}
              onClick={async () => {
                if (!formikProps.values.does_not_have_insurance_information) {
                  // reset insurance company field
                  await formikProps.setFieldValue("insurance_company", "");
                }
                await formikProps.setFieldValue(
                  "does_not_have_insurance_information",
                  !formikProps.values.does_not_have_insurance_information,
                );

                if (!formikProps.touched?.does_not_have_insurance_information) {
                  await formikProps.setFieldTouched("does_not_have_insurance_information", true);
                }
              }}
              value={formikProps.values.does_not_have_insurance_information}
            />
          </div>
          <div className="HCPPatientInfo-state_of_residence-container HCPWorkflowShared-form_field">
            <FormTextInput
              className="HCPWorkflowShared-form_field"
              formikProps={formikProps}
              name="state_of_residence"
              placeholder="State of Residence"
              readOnly
              title="State of Residence"
            />
            <p className="HCPPatientInfo-state_of_residence-text">
              The patient must be located in New York to be referred.
            </p>
          </div>
        </div>

        <div className="d-flex tw:justify-center">
          <Button
            buttonType={ButtonTypes.primaryV2}
            className="Btn Btn--pri self-signup-btn"
            // button is disabled if the form has not been touched yet
            // or if there are errors
            disabled={!formikProps.dirty || Object.keys(formikProps.errors).length}
            type="submit"
          >
            Submit Referral
          </Button>
        </div>
      </Form>
    );
  };

  const submitHandler = async (formData) => {
    const initialValues = buildDefaultHCPPatientFormValues();
    const formDataToSubmit = {
      ...initialValues,
      dob: formData.dob,
      email: formData.email,
      email_validation: formData.email_validation,
      first_name: formData.first_name,
      last_name: formData.last_name,
      mobile: formData.mobile,
      primary_insurance: {
        card_back: "",
        card_front: "",
        carrier: formData.insurance_company,
        group_id: "",
        member_id: "",
        policy_holder_name: "",
        policy_holder_question: "yes",
        state: "New York",
      },
    };

    formDataToSubmit["dob"] = `${formDataToSubmit["dob"].substring(0, 2)}/${formDataToSubmit[
      "dob"
    ].substring(2, 4)}/${formDataToSubmit["dob"].substring(4, 8)}`;
    formDataToSubmit["group_type"] = "hcp_program";

    // if we pass the empty string for this field then it will get ignored
    // by our entity creation logic
    if (formDataToSubmit.mobile === "") {
      formDataToSubmit.mobile = "None";
    }

    await update_state_and_get_next_node(
      {
        does_not_have_insurance_information: formData.does_not_have_insurance_information,
        patient_info: formDataToSubmit,
      },
      currentStepName,
      <span data-public>
        Success!{" "}
        <span data-private style={{ display: "inline" }}>
          {formData.first_name}
        </span>
        ’s referral has been submitted.
      </span>,
    );
  };

  return (
    <div className="container hcp-mobile-container px-sm-3 py-sm-5">
      <CardSecondary className="HCPWorkflowShared-card">
        <div className="HCPWorkflowShared-header_container">
          <h1 className="HCPWorkflowShared-title">
            {has_referred_already ? "Ready to refer another patient?" : "Nearly there!"}
          </h1>
          <p className="HCPWorkflowShared-sub_title">
            {has_referred_already
              ? "We've got your information saved. Just enter the patient's information and you're good to go."
              : "Enter the patient’s information to submit the referral."}
          </p>
        </div>
        <CustomForm
          initialValues={{
            dob: "",
            does_not_have_insurance_information: false,
            email: "",
            email_validation: "",
            first_name: "",
            insurance_company: "",
            last_name: "",
            mobile: "",
            state_of_residence: "New York",
          }}
          onSubmit={submitHandler}
          render={renderForm}
          validationSchema={HCP_VALIDATION_SCHEMA()}
        />
      </CardSecondary>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    configs: state.configs,
  };
};

export default connect(mapStateToProps, { showAlertWithAction })(HCPPatientInfo);
